/**
 * IS OUR REVIEW OF THIS HEAD DONE? The one answer (plan 2026-11-03/102 slice 04).
 *
 * Owner, 2026-11-03: "review is only [done] when all comments have been posted". Since the
 * review outbox (slice 03) a review has two halves that end at different instants: the box's
 * TASK ends when it seals the outbox, and the review is DONE only when the drain posts the
 * final part and writes `review_posted`. A reader that asked only the task's status read a
 * `completed` task with an undelivered outbox as "not in flight, not unserved": on a required
 * review the merge gate then refused `review_absent` and babysit minted a SECOND paid review
 * of a head whose first one was still posting.
 *
 * `@developerz/db` `readReviewDelivery` answers this in one statement, and every reader of
 * review completion takes its answer from there (`scripts/review-delivery-one-seam.test.ts`
 * rejects a new raw read). Precedence, highest first: `posting` > `running` > `unserved` >
 * `delivered` > `none` — a newer run's outbox or task beats an older verdict.
 */

import { z } from 'zod';

/** "Posting k of N": how far the drain is through one sealed outbox. */
export type ReviewPostingProgress = {
  /** Comment rows posted so far (`review_outbox.posted`). */
  readonly posted: number;
  /** Comment rows the seal required (`review_outbox.total`). */
  readonly total: number;
  /**
   * The drain dead-lettered the outbox (`dead_at` set): nothing will post it without an
   * operator. The PR stays held — never silently delivered or dropped (slice 03 step 10).
   */
  readonly stuck: boolean;
  /** When the box sealed it. */
  readonly since: Date;
  /**
   * `now() - sealed_at` on the DATABASE clock at the read: the age the escalation compares to
   * {@link REVIEW_POSTING_ESCALATE_AFTER_MS}. Relative, because the babysit decision core is
   * clockless (`entities/babysit/observation.ts`: ages, never an instant plus a `now`).
   */
  readonly ageMs: number;
};

/** Why an ENDED review of the head did not serve it (`hasUnservedPlatformReviewWithoutVerdict`). */
export type ReviewUnservedReason = 'review_unserved' | 'failed';

export type ReviewDelivery =
  /** No review of ours for this head: none minted, or every one ended without a hold. */
  | { readonly kind: 'none' }
  /** A `pr_review` task for the head is `pending` / `assigned` / `in_progress`. */
  | { readonly kind: 'running'; readonly taskId: string }
  /** The box finished and sealed its review; the drain is still posting it. */
  | ({
      readonly kind: 'posting';
      readonly taskId: string;
      readonly outboxId: string;
    } & ReviewPostingProgress)
  /** The head's newest `review_posted` carries a verdict. */
  | { readonly kind: 'delivered'; readonly taskId: string; readonly verdict: string }
  /** Our review of the head ended unserved or failed, with no verdict since (B1, #5958). */
  | { readonly kind: 'unserved'; readonly taskId: string; readonly reason: ReviewUnservedReason };

export type ReviewDeliveryKind = ReviewDelivery['kind'];

/**
 * How long an outbox may post before babysit escalates it, stuck or not: the 07 trip
 * ("an outbox posting longer than 2 h"). The failure it bounds is a PR held silently on a
 * drain that is alive but never finishes (a cooldown that keeps re-arming); a dead-lettered
 * outbox (`stuck`) escalates at once. A 103-comment review at the slowest pace measured
 * (#969: secondary-limited for 30 min) finishes well inside it.
 */
export const REVIEW_POSTING_ESCALATE_AFTER_MS = 2 * 60 * 60_000;

/** Is the review of this head still COMING (a verdict can still land)? */
export function reviewDeliveryPending(d: ReviewDelivery): boolean {
  return d.kind === 'running' || d.kind === 'posting';
}

/** The posting progress, or null for every other answer. */
export function reviewPostingOf(d: ReviewDelivery): ReviewPostingProgress | null {
  return d.kind === 'posting'
    ? { posted: d.posted, total: d.total, stuck: d.stuck, since: d.since, ageMs: d.ageMs }
    : null;
}

/** A posting hold past its trip, or dead-lettered: escalate rather than keep waiting. */
export function reviewPostingOverdue(p: Pick<ReviewPostingProgress, 'stuck' | 'ageMs'>): boolean {
  return p.stuck || p.ageMs >= REVIEW_POSTING_ESCALATE_AFTER_MS;
}

/**
 * ONE REVIEW RUN'S DELIVERY, read off that run's own `review_outbox` row (plan 102 slice 05):
 * what a surface listing runs shows beside the task's status, so a `completed` run whose
 * comments are still going up reads "posting k of N", never "done" (owner, 2026-11-03: "pr
 * still shows in review until we post all comments"). NULL on the wire for a run with no
 * outbox: every run before the box writer (slice 06), and every run whose box posted in-box.
 *
 * Not {@link ReviewDelivery}: that answers "is the review of this HEAD done" across runs,
 * with precedence; this is one run's own row, no precedence. The kinds fold the outbox
 * states (`@developerz/db` `REVIEW_OUTBOX_STATES`) to what a reader acts on:
 *   - `saving`    — `open`: the box is still putting comments; the run is not finished;
 *   - `posting`   — `sealed` / `posting`: the drain is posting; `posted` of `total` landed;
 *   - `delivered` — every comment and the verdict posted;
 *   - `retired`   — the drain ended it without posting the rest (a closed PR, a gone
 *     installation): its own named exit, never read as delivered.
 */
export const REVIEW_RUN_DELIVERY_KINDS = ['saving', 'posting', 'delivered', 'retired'] as const;
export type ReviewRunDeliveryKind = (typeof REVIEW_RUN_DELIVERY_KINDS)[number];

export type ReviewRunDelivery = {
  readonly kind: ReviewRunDeliveryKind;
  /** Comment rows posted so far (`review_outbox.posted`). */
  readonly posted: number;
  /** Comment rows the outbox holds (`review_outbox.total`). */
  readonly total: number;
};

/** The wire face (`delivery` on `GET /v1/prs/:id/review-runs` and `GET /v1/repos/:o/:r/reviews`). */
export const ReviewRunDeliveryWireSchema = z.object({
  kind: z.enum(REVIEW_RUN_DELIVERY_KINDS),
  posted: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type ReviewRunDeliveryWire = z.infer<typeof ReviewRunDeliveryWireSchema>;
